import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForms, type Form } from '../../api/forms.api';
import type { FormPathType, FormStatus } from '@engage/types';
import { NewFormModal } from './NewFormModal';
import { Btn, Icon, ICONS, PathChip, StatusChip } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

const STATUS_OPTIONS: { value: FormStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Status · All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const PATH_OPTIONS: { value: FormPathType | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Path · All' },
  { value: 'CANDIDATE', label: 'Candidate' },
  { value: 'CONTACT', label: 'Contact' },
];

export function FormsPage() {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<FormStatus | 'ALL'>('ALL');
  const [filterPath, setFilterPath] = useState<FormPathType | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  const { data, isLoading } = useForms();

  const rows = useMemo(() => {
    const base = data ?? [];
    const q = search.trim().toLowerCase();
    return base.filter((f) => {
      if (filterStatus !== 'ALL' && f.status !== filterStatus) return false;
      if (filterPath !== 'ALL' && f.path_type !== filterPath) return false;
      if (q && !f.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, filterStatus, filterPath, search]);

  const counts = useMemo(() => {
    const base = data ?? [];
    return {
      total: base.length,
      published: base.filter((f) => f.status === 'PUBLISHED').length,
      drafts: base.filter((f) => f.status === 'DRAFT').length,
    };
  }, [data]);

  return (
    <div style={{ padding: '26px 30px', fontFamily: FORM_UI_FONT, color: C.ink }}>
      {/* header */}
      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <div>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>Engage › Forms</div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.indigo,
              margin: '4px 0 4px',
            }}
          >
            Forms
          </h1>
          <div style={{ fontSize: 12.5, color: C.sub }}>
            {counts.total} forms · {counts.published} published · {counts.drafts} drafts · sourced
            &amp; synced with JobTetris
          </div>
        </div>
        <Btn kind="primary" onClick={() => setNewOpen(true)}>
          <Icon d={ICONS.plus} size={15} color="#fff" />
          Create form
        </Btn>
      </div>

      {/* filters */}
      <div style={{ display: 'flex', gap: 10, margin: '20px 0 14px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '0 0 300px' }}>
          <span style={{ position: 'absolute', left: 11, top: 9 }}>
            <Icon d={ICONS.search} size={16} color={C.faint} />
          </span>
          <input
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px 9px 34px',
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              fontFamily: FORM_UI_FONT,
              fontSize: 13,
              background: '#fff',
              color: C.ink,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <FilterSelect
          value={filterPath}
          onChange={(v) => setFilterPath(v as FormPathType | 'ALL')}
          options={PATH_OPTIONS}
        />
        <FilterSelect
          value={filterStatus}
          onChange={(v) => setFilterStatus(v as FormStatus | 'ALL')}
          options={STATUS_OPTIONS}
        />
      </div>

      {/* table or empty */}
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={() => setNewOpen(true)} />
      ) : (
        <FormsTable rows={rows} onRowClick={(id) => navigate(`/forms/${id}`)} />
      )}

      {/* explainer cards */}
      <ExplainerCards />

      {newOpen && (
        <NewFormModal
          onClose={() => setNewOpen(false)}
          onCreated={(form) => {
            setNewOpen(false);
            navigate(`/forms/${form.id}`);
          }}
        />
      )}
    </div>
  );
}

function FilterSelect<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          appearance: 'none',
          padding: '9px 32px 9px 13px',
          border: `1px solid ${C.line}`,
          borderRadius: 9,
          fontSize: 13,
          fontFamily: FORM_UI_FONT,
          fontWeight: 600,
          color: C.indigoSoft,
          background: '#fff',
          cursor: 'pointer',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span style={{ position: 'absolute', right: 11, top: 11, pointerEvents: 'none' }}>
        <Icon d={ICONS.chev} size={13} color={C.faint} sw={2} />
      </span>
    </div>
  );
}

function FormsTable({ rows, onRowClick }: { rows: Form[]; onRowClick: (id: string) => void }) {
  const navigate = useNavigate();
  const GRID = '1fr 120px 110px 130px 120px 60px';
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: `1px solid ${C.line}`,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID,
          padding: '13px 20px',
          borderBottom: `1px solid ${C.line}`,
          fontSize: 11.5,
          fontWeight: 700,
          color: C.faint,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        <div>Form</div>
        <div>Path</div>
        <div>Submissions</div>
        <div>Status</div>
        <div>Last updated</div>
        <div />
      </div>
      {rows.map((f, i) => {
        const count = f.submission_count ?? 0;
        return (
          <div
            key={f.id}
            onClick={() => onRowClick(f.id)}
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              padding: '15px 20px',
              alignItems: 'center',
              borderBottom: i < rows.length - 1 ? `1px solid ${C.line}` : 'none',
              cursor: 'pointer',
            }}
          >
            <div>
              <Link
                to={`/forms/${f.id}`}
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: C.ink,
                  textDecoration: 'none',
                }}
              >
                {f.name}
              </Link>
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>
                {f.description ?? '-'}
              </div>
            </div>
            <div>
              <PathChip type={f.path_type} />
            </div>
            <div>
              {count > 0 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    // Don't also trigger the row → builder navigation.
                    e.stopPropagation();
                    navigate(`/forms/${f.id}/submissions`);
                  }}
                  title="View submissions"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 999,
                    border: `1px solid ${C.teal}`,
                    background: '#fff',
                    color: C.tealDark,
                    fontFamily: FORM_UI_FONT,
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {count.toLocaleString()}
                  <Icon d={ICONS.chev} size={12} color={C.tealDark} sw={2} />
                </button>
              ) : (
                <span style={{ fontSize: 12.5, color: C.faint }}>0</span>
              )}
            </div>
            <div>
              <StatusChip status={f.status} />
            </div>
            <div style={{ fontSize: 12.5, color: C.sub }}>{relativeTime(f.updated_at)}</div>
            <div style={{ textAlign: 'right', color: C.faint }}>
              <Icon d={ICONS.dots} size={18} color={C.faint} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExplainerCards() {
  return (
    <div style={{ display: 'flex', gap: 16, marginTop: 18 }}>
      {(
        [
          {
            type: 'CANDIDATE' as const,
            heading: 'Candidate forms',
            body:
              'Tokenised "update my details" links sent to a known candidate. Updates flow back to the JobTetris candidate record, with blank-out protection and value bounds.',
          },
          {
            type: 'CONTACT' as const,
            heading: 'Contact forms',
            body:
              'Open public forms for client website embeds, gated content, marketing capture and event registration. Submissions create or append to a contact.',
          },
        ]
      ).map((card) => (
        <div
          key={card.type}
          style={{
            flex: 1,
            background: '#fff',
            borderRadius: 12,
            border: `1px solid ${C.line}`,
            borderTop: `3px solid ${card.type === 'CANDIDATE' ? C.candidate : C.contact}`,
            padding: '16px 18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
            <PathChip type={card.type} size="lg" />
            <span style={{ fontWeight: 700, fontSize: 14, color: C.indigo }}>{card.heading}</span>
          </div>
          <div style={{ fontSize: 12.5, color: C.sub, lineHeight: 1.55 }}>{card.body}</div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: `1px solid ${C.line}`,
        padding: '40px 28px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: C.candidateBg,
          color: C.indigo,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        <Icon d={ICONS.forms} size={22} color={C.indigo} />
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: C.indigo, margin: '0 0 6px' }}>
        No forms yet
      </h2>
      <p style={{ fontSize: 13, color: C.sub, margin: '0 0 18px', maxWidth: 480, marginInline: 'auto' }}>
        Forms come in two flavours: <strong style={{ color: C.indigo }}>candidate</strong> forms
        send a tokenised "update my details" link to known candidates, and{' '}
        <strong style={{ color: C.tealDark }}>contact</strong> forms are open public forms for
        embeds, gated content, and marketing capture.
      </p>
      <Btn kind="primary" onClick={onCreate}>
        <Icon d={ICONS.plus} size={15} color="#fff" /> Create your first form
      </Btn>
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}
